import { useQuery } from "@tanstack/react-query";
import { familyPaymentAllocationQueryKey } from "@/features/balance/utils/family-payment-allocation";
import { useAccessToken, useTenantData } from "@/hooks";
import { createAuthenticatedApi } from "@/lib/utils";
import { toRequestCurrency } from "@/lib/utils/currency";
import { studioTodayYmd } from "@/lib/utils/date/studio-clock";
import { PaymentApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";

interface UseFamilyPaymentAllocationGetParams {
  userId: number;
  enabled?: boolean;
}

export const useFamilyPaymentAllocationGet = ({
  userId,
  enabled = true,
}: UseFamilyPaymentAllocationGetParams) => {
  const accessToken = useAccessToken();
  const { tenantId, tenantData } = useTenantData();
  const billingDateYmd = studioTodayYmd(new Date(), tenantData?.timeZone);
  const currency = toRequestCurrency(tenantData?.currency);

  return useQuery({
    queryKey: familyPaymentAllocationQueryKey(userId, billingDateYmd, currency),
    queryFn: async () => {
      if (!accessToken) throw new Error("Authentication token is required");
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      try {
        const rows = await service.apiPaymentsFamilyPaymentAllocationGet({
          userId,
          currency,
          billingDate: billingDateYmd,
          xTenantID: tenantId,
        });
        return rows ?? [];
      } catch (error) {
        if (
          error instanceof ResponseError &&
          (error.response.status === 404 || error.response.status === 403)
        ) {
          return [];
        }
        throw error;
      }
    },
    enabled:
      enabled !== false &&
      !!accessToken &&
      !!userId &&
      !!tenantId &&
      !!currency,
    staleTime: 2 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
